// src/services/imageService.js
import { collection, getDocs } from "firebase/firestore";
import { db } from "../firebase";

const CACHE_KEY = "acby-image-urls";
const memoryCache = new Map();

const readSessionCache = () => {
  try {
    const raw = sessionStorage.getItem(CACHE_KEY);
    return raw ? JSON.parse(raw) : {};
  } catch {
    return {};
  }
};

const writeSessionCache = (entries) => {
  try {
    sessionStorage.setItem(CACHE_KEY, JSON.stringify(entries));
  } catch {
    // Ignore quota / private-mode failures
  }
};

const displayUrlFor = (docId) => `/images/${docId}.webp`;

const getCachedUrl = (docId) => {
  if (!docId) return null;
  if (memoryCache.has(docId)) return memoryCache.get(docId);

  const stored = readSessionCache();
  if (stored[docId]) {
    memoryCache.set(docId, stored[docId]);
    return stored[docId];
  }

  const displayUrl = displayUrlFor(docId);
  memoryCache.set(docId, displayUrl);
  return displayUrl;
};

const setCachedUrl = (docId, url) => {
  if (!docId || !url) return;
  memoryCache.set(docId, url);
  const stored = readSessionCache();
  stored[docId] = url;
  writeSessionCache(stored);
};

export const imageService = {
  getCachedUrl,
  getDisplayUrl: displayUrlFor,

  /**
   * Returns the optimized local/CDN image path for a picture document.
   */
  getImageUrlById: async (docId) => {
    if (!docId) return null;
    const url = displayUrlFor(docId);
    setCachedUrl(docId, url);
    return url;
  },

  /**
   * Batch-resolve display URLs without extra network reads.
   */
  getImageUrlsByIds: async (docIds = []) => {
    const result = {};
    [...new Set(docIds.filter(Boolean))].forEach((id) => {
      result[id] = displayUrlFor(id);
      setCachedUrl(id, result[id]);
    });
    return result;
  },

  /**
   * Fetches all images EXCEPT the ones already used
   * @param {string[]} excludedIds - Array of IDs used in CEO, About, and Staff
   */
  getGalleryImages: async (excludedIds = []) => {
    try {
      const colRef = collection(db, "pictures");
      const snapshot = await getDocs(colRef);

      return snapshot.docs
        .map((docSnap) => {
          const data = docSnap.data();
          const imageUrl = displayUrlFor(docSnap.id);
          setCachedUrl(docSnap.id, imageUrl);
          return {
            id: docSnap.id,
            ...data,
            originalUrl: data.imageUrl,
            imageUrl,
          };
        })
        .filter((img) => !excludedIds.includes(img.id));
    } catch (error) {
      console.error("Error fetching gallery images:", error);
      throw error;
    }
  },
};
